import { memo, type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import ReactECharts from 'echarts-for-react'
import type { DateTime } from 'luxon'

import { ChartNavControls } from '@/components/charts/history/ChartNavControls'
import { formatPeriodLabel, PERIOD_OPTIONS } from '@/components/charts/history/periodOptions'
import { type HistoryWindow, useHistoryNavigation } from '@/components/charts/history/useHistoryNavigation'
import { useAuth } from '@/hooks/useAuth'
import type { AlertMarker } from '@/insights/assets/historyGraph/types'
import { normalizeTimezone } from '@/insights/common/timezone'
import type { ApiService } from '@/types/dashboard-types'

import { DEFAULT_CHART_HEIGHT, MIN_CHART_CANVAS_PX } from './constants'
import type { ReeferFuelSample } from './types'
import { useFuelChartOptions } from './useFuelChartOptions'

import '@/insights/assets/historyGraph/echarts-setup'

interface ReeferFuelChartProps {
  data: ReeferFuelSample[]
  from: DateTime
  periodHours: number
  reload: (api: ApiService, window: HistoryWindow) => Promise<ReeferFuelSample[] | undefined>
  chartHeight?: number
  externalLoading?: boolean
  alertMarkers?: AlertMarker
  /** Where the date bar sits. The asset detail Graphs tab puts it on top, like the Temperature Graph. */
  navPosition?: 'top' | 'bottom'
  /** Controls at the left end of the date bar, e.g. a graph picker. */
  leading?: ReactNode
  /** Open on this window instead of `from` + `periodHours` (a period shared with other tabs). */
  initialWindow?: HistoryWindow
  /** Told the fetched window whenever it changes, and once on mount. */
  onWindowChange?: (window: HistoryWindow) => void
}

function ReeferFuelChart({
  data,
  from,
  periodHours,
  reload,
  chartHeight = DEFAULT_CHART_HEIGHT,
  externalLoading = false,
  alertMarkers,
  navPosition = 'bottom',
  leading,
  initialWindow,
  onWindowChange,
}: ReeferFuelChartProps) {
  const chartRef = useRef<ReactECharts>(null)
  const zone = normalizeTimezone(useAuth().user?.timezone)

  const [navHeight, setNavHeight] = useState(0)
  const navObserverRef = useRef<ResizeObserver | null>(null)
  const setNavContainer = useCallback((el: HTMLDivElement | null) => {
    navObserverRef.current?.disconnect()
    navObserverRef.current = null
    if (!el || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver((entries) => {
      setNavHeight(Math.ceil(entries[0]?.contentRect.height ?? 0))
    })
    observer.observe(el)
    navObserverRef.current = observer
  }, [])
  useEffect(() => () => navObserverRef.current?.disconnect(), [])

  const {
    currentData,
    loading,
    periodWindow,
    range,
    viewHours,
    isModified,
    navigate,
    changeViewHours,
    applyRange,
    jumpToDate,
    resetToDefault,
  } = useHistoryNavigation<ReeferFuelSample[]>({
    data,
    from,
    periodHours,
    zone,
    reload,
    initialWindow,
    onWindowChange,
  })

  const options = useFuelChartOptions(currentData, periodWindow, zone, alertMarkers)
  const periodLabel = PERIOD_OPTIONS.find((o) => String(viewHours) === o.value)?.label ?? formatPeriodLabel(viewHours)
  const canvasHeight = Math.max(MIN_CHART_CANVAS_PX, chartHeight - navHeight)

  useEffect(() => {
    chartRef.current?.getEchartsInstance()?.resize()
  }, [canvasHeight])

  const [isZoomed, setIsZoomed] = useState(false)
  const zoomTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    setIsZoomed(false)
  }, [periodWindow])

  useEffect(
    () => () => {
      if (zoomTimer.current) clearTimeout(zoomTimer.current)
    },
    []
  )

  const resetZoom = useCallback(() => {
    const instance = chartRef.current?.getEchartsInstance()
    if (instance) {
      instance.dispatchAction({ type: 'dataZoom', dataZoomIndex: 0, start: 0, end: 100 })
    }
    setIsZoomed(false)
  }, [])

  const onEvents = useMemo(
    () => ({
      datazoom: (params: { batch?: { start?: number; end?: number }[]; start?: number; end?: number }) => {
        const batch = params.batch?.[0] ?? params
        if (batch.start != null && batch.end != null) {
          const zoomed = batch.start > 0.5 || batch.end < 99.5
          if (zoomTimer.current) clearTimeout(zoomTimer.current)
          zoomTimer.current = setTimeout(() => {
            setIsZoomed(zoomed)
          }, 200)
        }
      },
      dblclick: () => {
        const instance = chartRef.current?.getEchartsInstance()
        if (instance) {
          instance.dispatchAction({ type: 'dataZoom', dataZoomIndex: 0, start: 0, end: 100 })
        }
        setIsZoomed(false)
      },
    }),
    []
  )

  const nav = (
    <div ref={setNavContainer} className={navPosition === 'top' ? 'flex flex-col gap-2 px-4' : undefined}>
      <ChartNavControls
        leading={leading}
        periodLabel={periodLabel}
        periodWindow={periodWindow}
        range={range}
        from={from}
        zone={zone}
        isModified={isModified}
        isZoomed={isZoomed}
        onResetZoom={resetZoom}
        navigate={navigate}
        jumpToDate={jumpToDate}
        resetToDefault={resetToDefault}
        showRange
        onRangeChange={applyRange}
      >
        <select
          className="select select-xs w-28 text-xs"
          value={String(viewHours)}
          onChange={(e) => changeViewHours(Number(e.target.value))}
          title="Period"
        >
          {PERIOD_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </ChartNavControls>
    </div>
  )

  return (
    <div>
      {navPosition === 'top' && nav}
      <div className="relative" style={{ minHeight: canvasHeight }}>
        <ReactECharts
          ref={chartRef}
          option={options}
          notMerge={true}
          lazyUpdate={true}
          onEvents={onEvents}
          opts={{ width: 'auto', height: 'auto' }}
          style={{ height: canvasHeight, width: '100%' }}
        />
        {(loading || externalLoading) && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/60">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-300 border-t-blue-600" />
          </div>
        )}
      </div>

      {navPosition === 'bottom' && nav}
    </div>
  )
}

export default memo(ReeferFuelChart)
